import { createFileRoute } from "@tanstack/react-router";
import { ContentPage } from "@/components/ContentPage";
import { FaqSection, faqJsonLd, type FaqItem } from "@/components/FaqSection";
import { BeforeAfterFrost } from "@/components/BeforeAfterFrost";
import { CallbackRail } from "@/components/CallbackRail";
import { SpecComparisonTable } from "@/components/SpecComparisonTable";
import { IMAGES, BBN_PHOTOS, BBN_REFERENCE_DEKOR } from "@/lib/images";

export const FAQS: FaqItem[] = [
  {
    q: "Vad är skillnaden mellan dekorfilm, frostfilm och etsad film?",
    a: "Dekorfilm är samlingsnamnet, medan frostfilm är den vanligaste varianten med matt, frostat utseende och etsad film efterliknar sandblästrat glas. Begreppen används ofta om varandra, men dekorfilm omfattar också mönstrade, färgade, randiga och tryckta filmer. Alla är polyesterbaserade, monteras invändigt och går att ta bort helt.",
  },
  {
    q: "Hur mycket ljus släpper en mattad/frostad folie igenom?",
    a: "Mattad dekorfolie släpper igenom omkring 90 % av dagsljuset, så ljusinsläppet ligger nära klarglas. Därför väljs folie ofta framför sandblästrat eller etsat glas på kontor med tydliga belysningskrav.",
  },
  {
    q: "Kan ni skära ut vår logotyp och text i folie?",
    a: "Ja. Vi skär logotyper, texter, mönster och konstnärlig grafik i frostfilm eller färgad folie. Skicka in en vektorfil (AI, EPS, SVG eller PDF) så får du en digital monteringspreview innan vi skär. Vid behov gör vi även grafisk design från grunden.",
  },
  {
    q: "Uppfyller frostfilm arbetsmiljöverkets krav på manifestationsglas?",
    a: "Ja. Arbetsmiljölagen kräver att stora glasytor i offentlig miljö ska vara märkta så att människor inte går in i dem — detta kallas personsäkerhet / manifestationsglas (AFS 2020:1). Frostfilm med horisontella band i ögonhöjd är ett av de vanligaste och mest accepterade sätten att uppfylla kravet — och samtidigt få estetisk avskärmning på köpet.",
  },
  {
    q: "Kan dekorfilmen tas bort om vi vill ändra layout senare?",
    a: "Ja, vi kan ta bort all dekorfilm vi monterar utan att skada glaset. Du kan alltså byta logotyp, mönster eller rumsindelning när företaget växer eller profilen ändras. Etsat och sandblästrat glas går inte att ändra på det sättet.",
  },
  {
    q: "Kan folie användas för totalt ljusblockerande på t.ex. takfönster eller lagerrum?",
    a: "Ja. Blockeringsfolie monteras oftast utvändigt och stänger ute i princip allt ljus. Den används vid takfönster, kupoler, lagerlokaler med ljuskänsligt gods, serverhallar och utvändiga ytor där full mörkläggning krävs. Vi tar in både helt opaka folier och olika nivåer av reducerande folie beroende på behovet.",
  },
  {
    q: "Kan ni renovera slitna dörrar, hissar, väggpaneler och whiteboards med folie?",
    a: "Ja. Dekorfolie ersätter i många fall målning, panelbyte eller helt nya inventarier. Vi förnyar dörrar, diskar, hissar, väggar och paneler — och kan även etablera nya integrerade whiteboard-ytor (med eller utan magnetfunktion) eller laga slitna befintliga whiteboards. Resultatet är snyggt och slittåligt utan stillestånd och utan att verksamheten störs av målningslukt eller byggdamm.",
  },
  {
    q: "Hur lång är leveranstiden för ett dekorfilm-projekt?",
    a: "För standardfrostfilm: 1–2 veckor från beställning till installation. För skurna logotyper och kundunika mönster: 2–4 veckor inklusive design, godkännande och produktion. Vid akuta behov, till exempel inflyttning till ett nytt kontor, kan vi arbeta snabbare.",
  },
  {
    q: "Fungerar dekorfilm på alla typer av glas?",
    a: "Ja, på alla rena glasytor — fönster, glasväggar, dörrar, glasräcken, speglar, glasbord. Filmen monteras invändigt på en torr, dammfri yta. Vi rekommenderar inte montering på glas med strukturerad yta (kornat glas, glasbetong) eftersom vidhäftningen då blir sämre.",
  },
  {
    q: "Vad kostar dekorfilm per m²?",
    a: "Standard frostfilm i hela bandbredder ligger typiskt på 600–900 kr/m² inklusive installation för normala projekt. Skurna logotyper och mönster prissätts per projekt beroende på komplexitet — vi ger alltid fast pris efter platsbesök.",
  },
  {
    q: "Hur lång är livslängden?",
    a: "Invändig dekorfilm håller normalt 10–15 år utan att tappa optik eller vidhäftning. Vi lämnar upp till 10 års produktgaranti. Filmen klarar normal fönsterputs och behöver inget underhåll. För utvändig folie räkna med 5–7 år beroende på UV-exponering.",
  },
];

export const Route = createFileRoute("/dekorfilm/")({
  head: () => ({
    meta: [
      { title: "Frostfilm & dekorfilm för glas i Skåne | BBN Nordic" },
      { name: "description", content: "Dekorfilm och frostfilm för glas — glasväggar, avskärmning, logotyper, text och renovering. Ekonomiskt, snabbt och fullt avlägsnbart. Auktoriserad installatör i hela Sverige." },
      { property: "og:title", content: "Frostfilm & dekorfilm för glas" },
      { property: "og:description", content: "Glasväggar, avskärmning, logotyper, text och renovering — allt med folie." },
      { property: "og:url", content: "https://bbnnordic.se/dekorfilm" },
      { property: "og:image", content: IMAGES.dekorfolieHero },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: "https://bbnnordic.se/dekorfilm" },
      { rel: "preload", as: "image", href: IMAGES.dekorfolieHero, fetchpriority: "high" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Installation av dekor- och frostfilm för glas",
          serviceType: "Dekorfilms-installation",
          provider: { "@type": "LocalBusiness", name: "BBN Nordic AB", url: "/" },
          areaServed: [
            { "@type": "AdministrativeArea", name: "Skåne" },
            { "@type": "City", name: "Göteborg" },
          ],
          image: IMAGES.dekorfolieHero,
          url: "/dekorfilm",
          brand: { "@type": "Brand", name: "3M" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Frostfilm & dekorfilm för glas",
          category: "Fönsterfilm / Dekorfilm",
          description: "Frostade ytor, mönster, logotyper, texter, avskärmning och renoveringsfolie för glasväggar, mötesrum, butiker, dörrar, paneler och whiteboards.",
          image: IMAGES.dekorfolieHero,
          brand: { "@type": "Brand", name: "3M" },
          url: "/dekorfilm",
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "SEK",
            lowPrice: "3000",
            priceSpecification: { "@type": "PriceSpecification", price: "3000", priceCurrency: "SEK" },
            availability: "https://schema.org/InStock",
            seller: { "@id": "/#organization" },
            areaServed: "SE",
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              applicableCountry: "SE",
              returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
            },
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "SEK" },
              shippingDestination: { "@type": "DefinedRegion", addressCountry: "SE" },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 5, unitCode: "DAY" },
                transitTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 14, unitCode: "DAY" },
              },
            },
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.8",
            reviewCount: "6",
            bestRating: "5",
            worstRating: "1",
          },
          review: [
            {
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              author: { "@type": "Person", name: "Andrée Johnsson" },
              reviewBody: "Kan varmt rekommenderas. Vi hade en undermålig solfilm som konkurrent satt upp, Marcus knackade på när han var hos grannen och sa att det där kan göras bättre.",
            },
            {
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              author: { "@type": "Person", name: "Christian Backe" },
              reviewBody: "Vi hade problem med att våra fönster släppte in väldigt mycket värme och valde en film som BBN satte med bästa resultat. Kan varmt rekommendera!",
            },
          ],
        }),

      },
      {
        type: "application/ld+json",
        children: JSON.stringify(faqJsonLd(FAQS)),
      },
    ],
  }),
  component: () => (
    <ContentPage
      disableCta
      eyebrow="TJÄNST · KONTORSPRIVACY & VARUMÄRKE"
      title="Frostfilm & dekorfilm för glas"
      subtitle="Med dekorfilm kan du skärma av mötesrum och sätta logotypen på entréglaset. Filmen kräver varken sandblästring eller ombyggnad och går att ta bort när du vill ändra utseendet."
      heroImage={IMAGES.dekorfolieHero}
      breadcrumbs={[{ label: "Dekorfilm" }]}
      priorityContent={
        <BeforeAfterFrost
          image={BBN_PHOTOS.kontorRullegardiner}
          alt="Kontor med rullgardiner — visualisering före och efter frostfilm"
        />
      }
      sections={[
        {
          eyebrow: "DÄRFÖR DEKORFILM",
          heading: "Insynsskydd, glasmarkering och dekor på samma yta.",
          paragraphs: [
            "Dekorfilm kan göra flera jobb på samma gång. Du får insynsskydd där det behövs, markering av glas enligt lagkraven och en plats för företagets logotyp. Vi använder också folie för att förnya slitna ytor utan ombyggnad. Monteringen tar timmar snarare än veckor.",
          ],
          bullets: [
            "GDPR-säker avskärmning av samtalsrum, receptioner och skärmar",
            "Uppfyller manifestationsglas enligt AFS 2020:1 (lagkrav)",
            "Varumärket direkt i glaset — receptionsvägg, entré, skyltfönster",
            "Renovering av slitna dörrar, paneler och whiteboards utan byte",
            "Behåller ~90 % dagsljus — släcker inte ner lokalen",
            "Fullt avlägsnbart när organisationen växer eller profilen ändras",
          ],
          facts: [
            { value: "10–20 %", label: "Av kostnaden för glasbyte", source: "Typprojekt, renovering" },
            { value: "0 dgr", label: "Stopp i verksamheten", source: "Monteras fönster för fönster" },
            { value: "10 år", label: "Produktgaranti invändigt", source: "BBN Nordic standard" },
          ],
          image: BBN_PHOTOS.dekorGlass,
          extra: (
            <SpecComparisonTable
              tone="light"
              caption="Dekor- och frostfilm — garanti och förväntad livslängd per användning."
              featureHeader="Folietyp / placering"
              sourceHeader="Underlag"
              columns={[
                { id: "warranty", label: "Produktgaranti", highlight: true },
                { id: "lifetime", label: "Förväntad livslängd" },
                { id: "use", label: "Typisk användning" },
              ]}
              rows={[
                {
                  feature: "Mattad / frostad folie, invändig",
                  values: {
                    warranty: "10 år",
                    lifetime: "10–15 år",
                    use: "Mötesrum, glasväggar, badrum",
                  },
                  source: "BBN Nordic standard",
                },
                {
                  feature: "Färgad folie (matt/blank/metallic), invändig",
                  values: {
                    warranty: "10 år",
                    lifetime: "10–15 år",
                    use: "Logotyp, profilering, dekor",
                  },
                  source: "BBN Nordic standard",
                },
                {
                  feature: "Skuren logotyp / text, invändig",
                  values: {
                    warranty: "10 år",
                    lifetime: "10–15 år",
                    use: "Reception, dörrar, skyltning",
                  },
                  source: "BBN Nordic standard",
                },
                {
                  feature: "Fullfärgsutskrift med vit underbas",
                  values: {
                    warranty: "5 år",
                    lifetime: "7–10 år",
                    use: "Stora dekorpartier, kampanjer",
                  },
                  source: "Tillverkargaranti",
                },
                {
                  feature: "UV-stabiliserad folie, utvändig",
                  values: {
                    warranty: "3 år",
                    lifetime: "5–7 år",
                    use: "Skyltfönster, fasaddekor",
                  },
                  source: "Tillverkargaranti, UV-exponering",
                },
              ]}
            />
          ),
        },
        {
          eyebrow: "VARFÖR BBN NORDIC",
          heading: "Vi skär och monterar din dekorfilm.",
          paragraphs: [
            "Vi är auktoriserade installatörer för 3M och Solar Screen och arbetar enbart med dekorfilm, solfilm och säkerhetsfilm. Vi skär filmen i vår egen plottermaskin, och våra montörer monterar tusentals m² folie varje år. Vi följer tillverkarens monteringsanvisningar så att produktgarantin gäller.",
          ],
          bullets: [
            "Auktoriserad installatör för 3M och Solar Screen",
            "Ni ser en digital skiss på era egna glas innan ni godkänner",
            "Egen skärmaskin — logotyper och text i vektorprecision",
            "Fast pris efter platsbesök, inga rörliga timpriser",
            "Slutbesiktning och dokumentation — upp till 10 års garanti",
            "Referenser från advokatbyråer, vård, banker, hotell, BRF:er",
          ],
          image: BBN_REFERENCE_DEKOR[4],
        },
        {
          eyebrow: "KONTORSPRIVACY",
          heading: "Frostfilm för mötesrum och samtalsrum.",
          paragraphs: [
            "Glasväggar släpper in ljus, men de gör också att andra kan se in. Frostfilm skärmar av mötesrum, samtalsbås och receptioner och behåller dagsljuset. För advokat- och revisionsbyråer, vårdmottagningar och banker finns ofta också GDPR-krav på att skydda klientdokument och patientuppgifter på skärmar från insyn.",
            "Standardkombinationen i kontorsglas är tre lager på samma yta: en hög frostzon för insynsskydd, ett markeringsband i ögonhöjd som uppfyller manifestationsglas enligt AFS 2020:1, och en valfri logotyp- eller textintegration. Allt på en folie, monterad på några timmar per glasparti.",
          ],
          bullets: [
            "Mötesrum, samtalsbås och fokusrum — visuell avskildhet utan ljustapp",
            "GDPR-säker avskärmning för advokat-, revisions- och finansrum",
            "Manifestationsglas (AFS 2020:1) — synliggör glasytan för säkerhet",
            "Patientsamtal i vård, tandläkare och kliniker",
            "Coworking & flexkontor — modulär dekor som byggs om vid expansion",
          ],
          facts: [
            { value: "~90 %", label: "Dagsljus släpps igenom", source: "Mattad frostfolie, typvärde" },
            { value: "100 %", label: "Insynsskydd", source: "AFS 2020:1 manifestationskrav" },
            { value: "10 år", label: "Produktgaranti", source: "Invändig montering" },
          ],
          image: BBN_REFERENCE_DEKOR[0],
        },
        {
          eyebrow: "VARUMÄRKE PÅ GLASET",
          heading: "Din logotyp och text på glaset.",
          paragraphs: [
            "I receptionen, på entrédörren och i konferensrummet kan du visa företagets logotyp och kontaktuppgifter. Vi skär logotyper och texter i frostfilm eller färgad folie och erbjuder även fullfärgsutskrift. Du får se en digital skiss före produktionen. Kanterna blir skarpa och dekoren ser ut som en del av glaset.",
          ],
          bullets: [
            "Logotyper i receptioner, entrédörrar och glasväggar",
            "Öppettider, kontaktuppgifter och vägvisning",
            "Reklamtext och säsongskampanjer i skyltfönster",
            "Skylttext och varumärkesprofilering i lobby och konferensrum",
            "Färgad folie i matt, blank, metallic (guld, silver, koppar) och neon",
            "Fullfärgsutskrift med vit underbas för djup färg på klarglas",
            "UV-stabiliserad folie för utomhusbruk (5–7 års livslängd)",
          ],
          extra: (
            <div className="flex flex-wrap gap-3">
              <a
                href="mailto:info@bbnnordic.se?subject=Layout%20f%C3%B6r%20logotyp%20%2F%20text%20p%C3%A5%20glas&body=Hej%2C%0A%0AVi%20vill%20ha%20en%20layout%20framtagen%20f%C3%B6r%20text%20eller%20logotyp%20p%C3%A5%20v%C3%A5rt%20glas.%20Vi%20bifogar%20vektorfil%20(AI%2FEPS%2FSVG%2FPDF)%20och%20m%C3%A5tt%20nedan.%0A%0AGlasets%20m%C3%A5tt%3A%0AAntal%20ytor%3A%0AFilm%C3%B6nskem%C3%A5l%20(frostat%2C%20f%C3%A4rg%2C%20fullf%C3%A4rg)%3A%0A%0AMvh"
                className="inline-flex items-center gap-2 rounded-full bg-gold text-navy px-5 py-3 text-sm font-semibold hover:bg-gold-bright transition-colors"
              >
                Mejla in layout för offert →
              </a>
              <a href="/offert" className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold hover:border-bbn-accent/40 transition-colors">
                Eller fyll i offertformuläret
              </a>
            </div>
          ),
          image: BBN_REFERENCE_DEKOR[1],
        },
        {
          eyebrow: "AVSKÄRMNING & LJUSKONTROLL",
          heading: "Från mittband till total mörkläggning.",
          paragraphs: [
            "Frostad eller mönstrad folie skapar insynsskydd där det behövs, samtidigt som dagsljuset bevaras. När full mörkläggning krävs — takfönster, lagerlokaler med ljuskänsligt gods, serverhallar — använder vi i stället blockeringsfolier utvändigt som stänger ute i princip allt ljus.",
          ],
          bullets: [
            "Fullständigt frostat — badrum, omklädningsrum, WC",
            "Mittband 80–120 cm högt — klassisk mötesrumsavskärmning",
            "Gradient (top-to-bottom) — mjukare visuellt uttryck",
            "Horisontella ränder, prickar, geometriska mönster",
            "Blockeringsfolie helt opak — takfönster, kupoler, lagerrum (utvändigt)",
            "Blockeringsfolie i färg — färgad opak folie för fasad, färgblock, wayfinding",
            "Blockeringsfolie med tryck — er logotyp eller grafik tryckt i opak folie (fasadprofilering, skyltfönster)",
            "Kundunik design — er egen grafik skuren eller tryckt i folien",
          ],
          image: BBN_REFERENCE_DEKOR[2],
        },
        {
          eyebrow: "RENOVERING & FÖRNYELSE",
          heading: "Uppfräschning utan glasbyte, panelbyte eller målning.",
          paragraphs: [
            "Med folie kan vi i många fall förnya ytor som annars hade behövt målas eller bytas ut. De som använder lokalen slipper byggdamm, lukt och buller. Kostnaden är typiskt 10–20 % av ett glasbyte, och vi monterar fönster för fönster så att verksamheten kan fortsätta ostört.",
            "Vi har också folier med naturtrogna mönster av trä, sten, marmor, betong och borstad metall. De används på hissar, dörrar, receptionsdiskar, väggpaneler och möbler när du vill ändra utseendet och behålla materialet under.",
          ],
          bullets: [
            "Uppdatering av åldrade glasväggar utan glasbyte",
            "Renovering av slitna dörrar, diskar, hissar, väggar, paneler",
            "Naturtrogna mönster — trä, sten, marmor, betong, borstad metall",
            "Reparation eller nyetablering av whiteboard-ytor (ev. magnetiska)",
            "Uppfräschning av entrépartier i bostadsrättsföreningar",
            "Hotellrenovering — frostning av badrum, dekor i lobby",
            "Butiksomprofilering — ny logotyp och färgsättning på skyltfönster",
            "Klotterskydd och säkerhetsmarkering på golv, trappor, ramper",
          ],
          image: BBN_REFERENCE_DEKOR[3],
        },
        {
          eyebrow: "BILDEKORATIONER",
          heading: "Fordonsdekor — från budbil till hela vagnparken.",
          paragraphs: [
            "Vi levererar bildekor till företagsbilar, transportfordon, servicebilar och specialfordon. Du kan välja skurna logotyper, färgade sidopartier eller hel- och delfoliering. Vi använder UV-stabiliserad folie och visar en digital skiss före produktionen.",
          ],
          bullets: [
            "Företagsbilar och servicebilar — logotyp, kontaktinfo, telefonnummer",
            "Transportfordon och lastbilar — hel- eller delfoliering",
            "Wayfinding och säkerhetsmarkeringar",
            "Vinylfolie i matt, blank eller strukturerad yta",
            "Långtidsstabil UV-folie för Nordens klimat",
          ],
          image: BBN_REFERENCE_DEKOR[5],
        },
        {
          eyebrow: "ANVÄNDNINGSOMRÅDEN",
          heading: "Branscher där vi arbetar dagligen.",
          bullets: [
            "Kontor & coworking — avskärmade mötesrum, manifestationsband",
            "Advokat- & revisionsfirmor — GDPR-säker avskärmning av samtalsrum",
            "Vård- och tandläkarmottagningar — sekretess i receptioner",
            "Butiker & restauranger — skyltfönster, varumärkesprofilering",
            "Skolor & förskolor — insynsskydd och manifestationsglas (personsäkerhet)",
            "Hotell — frostade dörrar, dekor i lobby och konferensytor",
            "Bostadsrättsföreningar — gemensamhetsutrymmen, garageportar i glas",
            "Industri & lager — vägvisning, säkerhetsmarkering, blockeringsfolie",
            "Sjukhus och offentliga miljöer — vägvisning, manifestation",
            "Fordonspark & servicebilar — bildekor, logotyp, kontaktinfo",
            "Privatpersoner — badrumsfönster, glasdörrar, balkongavskärmning",
          ],
          image: BBN_REFERENCE_DEKOR[5],
        },
        {
          eyebrow: "STANDARDDESIGN A–I",
          heading: "Nio färdiga foliedesigner att välja mellan.",
          paragraphs: [
            "Utöver skurna logotyper och kundunik grafik erbjuder vi nio standarddesigner — märkta A till I — med prickar, ränder, rutmönster och gradienter. De passar för avskärmning, manifestationsband och profilering där man inte vill ha en helt kundunik produktion.",
            "Alla designer levereras i standardhöjd 114 cm (full bandbredd) och kan monteras i valfri höjd eller kombineras till dubbla band. Manifestationsränder i ögonhöjd (personsäkerhet enligt AFS 2020:1) tas typiskt fram som 15 cm bred rand — vi anpassar exakt placering efter glasets höjd.",
          ],
          bullets: [
            "Design A–C — heltäckande frostat, satinerade ytor och gradienter",
            "Design D–F — horisontella ränder i olika täthet (typiskt 5, 10 och 15 mm ränder)",
            "Design G–I — prickmönster (dots) och rutmönster i varierande täthet",
            "Standardhöjd 114 cm per bandbredd — kan skarvas för höga glaspartier",
            "Manifestationsrand 15 cm i ögonhöjd (personsäkerhet, AFS 2020:1)",
            "Alla nio finns som fysiskt prov att beställa hem eller se på plats",
            "Kombineras fritt med skuren logotyp eller egen text",
          ],
          image: BBN_REFERENCE_DEKOR[1],
        },
        {

          eyebrow: "VÅR PROCESS",
          heading: "Från idé till färdig glasyta på 2–4 veckor.",
          paragraphs: [
            "Standardproduktioner (frostband, geometriska mönster) eller fullt kundanpassade projekt med skuren logotyp och konstnärlig grafik. Stora projekt projekteras tillsammans med er arkitekt eller inredare, och vi lägger upp monteringen så att den stör er verksamhet så lite som möjligt.",
          ],
          bullets: [
            "1. Idé- och designmöte — fysiskt eller digitalt",
            "2. Vi tar fram monteringspreview i 3D eller foto-mockup",
            "3. Ni godkänner färg, storlek och placering",
            "4. Folien skärs i vår skärmaskin (vektorbaserad precision)",
            "5. Montering av certifierade montörer — torr, dammfri, luktfri",
            "6. Slutbesiktning och dokumentation — upp till 10 års garanti",
          ],
          image: BBN_PHOTOS.installerPortrait,
        },
      ]}
      related={[
        { to: "/guider/dekorfilm-frostat-glas", label: "Guide: frostat glas", desc: "När frostfilm vinner över sandblästring." },
        { to: "/sakerhetsfilm", label: "Säkerhetsfilm", desc: "Kombinera dekor med splitterskydd." },
        { to: "/referenser", label: "Referenser", desc: "Se våra utförda dekorprojekt." },
      ]}
    >
      <FaqSection
        heading="Vanliga frågor om dekorfilm och frostfilm."
        items={FAQS}
      />
      <CallbackRail product="Dekorfilm" />
    </ContentPage>
  ),
});
